import React, { useState, useEffect } from 'react';
import { ChordInspectorData } from '../types';
import GuitarChordNavigator from './GuitarChordNavigator';
import { getGuitarVoicings } from '../utils/guitarChords';

interface ChordInspectorProps {
  data: ChordInspectorData | null;
  mode: 'piano' | 'guitar';
  isPinned?: boolean;
  onTogglePin?: () => void;
  onClose?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface DiagramProps {
  notes: string[];
  rootNote: string;
  bassNote: string | null;
}

const NOTE_MAP: { [key: string]: number } = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };
const SANITIZE_NOTE = (note: string) => note.replace('♯', '#').replace('♭', 'b');


const calculateVoicing = (notes: string[], rootNote: string, bassNote: string | null): number[] => {
    const rootPc = NOTE_MAP[SANITIZE_NOTE(rootNote)];
    if (typeof rootPc !== 'number') return [];
    
    const allPcs = notes.map(n => NOTE_MAP[SANITIZE_NOTE(n)]).filter((pc): pc is number => pc !== undefined);
    const bassPc = bassNote ? NOTE_MAP[SANITIZE_NOTE(bassNote)] : null;

    const sortedIntervals = [...new Set(allPcs)]
        .map(pc => (pc - rootPc + 12) % 12)
        .sort((a, b) => a - b);

    const MIDDLE_C = 60;
    let lastMidi = MIDDLE_C - 5; 
    
    let voicedMidiNotes: number[] = [];

    for (const interval of sortedIntervals) {
        const currentPc = (rootPc + interval) % 12;
        let currentMidi = 0;
        
        const startOctave = Math.floor(lastMidi / 12);
        for (let oct = startOctave; oct < startOctave + 3; oct++) {
            currentMidi = currentPc + oct * 12;
            if (currentMidi > lastMidi) break;
        }
        voicedMidiNotes.push(currentMidi);
        lastMidi = currentMidi;
    }

    if (typeof bassPc === 'number' && voicedMidiNotes.length > 0) {
        const lowestChordToneMidi = Math.min(...voicedMidiNotes);
        let bassMidi = 0;

        const startOctave = Math.floor(lowestChordToneMidi / 12);
         for (let oct = startOctave; oct > startOctave - 3; oct--) {
            bassMidi = bassPc + oct * 12;
            if (bassMidi < lowestChordToneMidi) break;
        }

        voicedMidiNotes = voicedMidiNotes.filter(n => n % 12 !== bassPc);
        voicedMidiNotes.unshift(bassMidi);
    }

    return voicedMidiNotes;
};


const PianoDiagram: React.FC<DiagramProps> = ({ notes, rootNote, bassNote }) => {
    const ROOT_COLOR = 'rgba(239, 68, 68, 0.7)'; // Red-500 with 70% opacity
    const RAINBOW_COLORS = [
      'rgba(249, 115, 22, 0.7)', // Orange-500
      'rgba(234, 179, 8, 0.7)',  // Yellow-500
      'rgba(132, 204, 22, 0.7)', // Lime-500
      'rgba(16, 185, 129, 0.7)', // Emerald-500
      'rgba(6, 182, 212, 0.7)',   // Cyan-500
      'rgba(99, 102, 241, 0.7)', // Indigo-500
      'rgba(168, 85, 247, 0.7)'  // Purple-500
    ];

    const rootPc = NOTE_MAP[SANITIZE_NOTE(rootNote)];
    if (typeof rootPc !== 'number') {
        return <div className="relative w-full h-[80px] bg-gray-800 rounded-md" />;
    }
    
    const voicedMidiNotes = new Set(calculateVoicing(notes, rootNote, bassNote));
    
    const colorMap = new Map<number, string>();
    colorMap.set(rootPc, ROOT_COLOR);
    
    const uniquePcs = [...new Set(notes.map(n => NOTE_MAP[SANITIZE_NOTE(n)]).filter((pc): pc is number => pc !== undefined))];
    const otherPcs = uniquePcs.filter(pc => pc !== rootPc);
    
    const sortedIntervalsFromRoot: number[] = [];
    for (const pc of otherPcs) {
        if (typeof pc === 'number') {
          sortedIntervalsFromRoot.push((pc - rootPc + 12) % 12);
        }
    }
    sortedIntervalsFromRoot.sort((a, b) => a - b);

    sortedIntervalsFromRoot.forEach((interval, index) => {
        const pc = (rootPc + interval) % 12;
        colorMap.set(pc, RAINBOW_COLORS[index % RAINBOW_COLORS.length]);
    });

    const START_MIDI = 48; // C3
    const NUM_OCTAVES = 3;
    const keys = [];
    for (let i = 0; i < NUM_OCTAVES * 12; i++) {
        const midi = START_MIDI + i;
        const pc = midi % 12;
        keys.push({ midi, pc, isBlack: [1, 3, 6, 8, 10].includes(pc) });
    }

    const whiteKeys = keys.filter(k => !k.isBlack);
    const blackKeys = keys.filter(k => k.isBlack);
    const whiteKeyWidth = 100 / whiteKeys.length;

    return (
        <div className="relative w-full h-[140px] bg-gray-800 rounded-md pt-8 pb-2 px-2">
            <div className="relative w-full h-full bg-gray-600 rounded-md overflow-visible">
                {/* White keys */}
                <div className="flex h-full w-full">
                    {whiteKeys.map(({ midi, pc }) => (
                        <div key={midi} className="relative flex-1 bg-white border-r border-gray-400">
                            {voicedMidiNotes.has(midi) && (
                                <div
                                    className="absolute inset-0"
                                    style={{ backgroundColor: colorMap.get(pc) }}
                                />
                            )}
                        </div>
                    ))}
                </div>
                {/* Black keys */}
                {blackKeys.map(({ midi, pc }) => {
                    const precedingWhiteKeys = whiteKeys.filter(wk => wk.midi < midi).length;
                    const left = (precedingWhiteKeys * whiteKeyWidth) - (whiteKeyWidth * 0.35);
                    return (
                        <div key={midi} 
                             className="absolute top-0 h-[60%] z-10 bg-black rounded-b-sm border-x-2 border-b-2 border-gray-700"
                             style={{ left: `${left}%`, width: `${whiteKeyWidth * 0.7}%` }}>
                            {voicedMidiNotes.has(midi) && (
                                <div
                                    className="absolute inset-0 rounded-b-sm"
                                    style={{ backgroundColor: colorMap.get(pc) }}
                                />
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    );
};





const ChordInspector: React.FC<ChordInspectorProps> = ({ data, mode, isPinned = false, onTogglePin, onClose, onMouseEnter, onMouseLeave }) => {
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window;
      setIsMobileOrTablet(width <= 1024 || isTouchDevice);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  if (!data) return null;

  const { notes, rootNote, bassNote, position } = data;

  // Calculate inspector dimensions
  const inspectorHeight = mode === 'guitar' ? 350 : 200; // Approximate heights
  const inspectorWidth = mode === 'guitar' ? 220 : 280;
  
  // Default positioning: centered above the chord
  let translateX = '-50%';
  let translateY = '-110%';
  
  // Adjust horizontal position if too close to edges
  if (position.x < inspectorWidth / 2 + 10) {
    translateX = '-10%'; // Too close to left edge
  } else if (position.x > window.innerWidth - inspectorWidth / 2 - 10) {
    translateX = '-90%'; // Too close to right edge
  }
  
  // Adjust vertical position if too close to top
  if (position.y < inspectorHeight + 20) {
    translateY = '20%'; // Too close to top, show below instead
  }

  const style: React.CSSProperties = {
    position: 'fixed',
    top: `${position.y}px`,
    left: `${position.x}px`,
    transform: `translate(${translateX}, ${translateY})`,
    pointerEvents: 'auto',
    zIndex: 100,
    transition: 'opacity 0.1s ease-in-out',
    opacity: 1,
    width: `${inspectorWidth}px`,
  };


  return (
    <div 
      style={style} 
      className="bg-gray-900 border border-cyan-400/50 rounded-lg shadow-xl backdrop-blur-sm bg-opacity-80 relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Pin/Close button for mobile/tablet */}
      {isMobileOrTablet && (onTogglePin || onClose) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isPinned && onClose) {
              onClose();
            } else if (onTogglePin) {
              onTogglePin();
            }
          }}
          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center hover:bg-gray-700/50 rounded transition-colors"
          style={{ zIndex: 50 }}
          aria-label={isPinned ? "Close inspector" : "Pin inspector"}
        >
          <svg 
            viewBox="0 0 512 512" 
            className="w-5 h-5"
            style={{ fill: isPinned ? '#ef4444' : '#06b6d4' }}
          >
            {/* Cross (X) - only visible when pinned */}
            {isPinned && (
              <path d="M313.752,112.915c-8.331-8.331-21.839-8.331-30.17,0L256,140.497l-27.582-27.582c-8.331-8.331-21.839-8.331-30.17,0c-8.331,8.331-8.331,21.839,0,30.17l27.582,27.582l-27.582,27.582c-8.331,8.331-8.331,21.839,0,30.17c8.331,8.331,21.839,8.331,30.17,0L256,200.837l27.582,27.582c8.331,8.331,21.839,8.331,30.17,0c8.331-8.331,8.331-21.839,0-30.17l-27.582-27.582l27.582-27.582C322.083,134.754,322.083,121.246,313.752,112.915z"/>
            )}
            {/* Pin shape (always visible) */}
            <path d="M426.667,170.667C426.667,76.41,350.257,0,256,0S85.333,76.41,85.333,170.667c0,87.031,65.146,158.843,149.333,169.342v150.658c0,11.782,9.551,21.333,21.333,21.333s21.333-9.551,21.333-21.333V340.008C361.521,329.51,426.667,257.698,426.667,170.667z M256,298.667c-70.693,0-128-57.307-128-128s57.307-128,128-128s128,57.307,128,128S326.693,298.667,256,298.667z"/>
          </svg>
        </button>
      )}

      <div className={isMobileOrTablet && (onTogglePin || onClose) ? 'pt-2 overflow-visible' : 'p-3 overflow-visible'}>
        {mode === 'piano' ? (
          <PianoDiagram notes={notes} rootNote={rootNote} bassNote={bassNote} />
        ) : (
          <GuitarChordNavigator voicings={getGuitarVoicings(data.chord)} />
        )}
      </div>
    </div>
  );
};

export default ChordInspector;